import { Controller } from 'stimulus'
import { useDebounce, useDispatch, useWindowResize } from 'stimulus-use'

import { keys } from '@app/components/atomic/constants'
import I18n from '@app/javascript/i18n/index.js.erb'

/**
 * @namespace Menu
 * {@link https://adamsilver.io/blog/building-an-accessible-autocomplete-control/}
 */
export default class extends Controller {
  static targets = ['trigger', 'labelContent', 'list', 'focusable', 'liveRegion']

  /**
     * Set up certain keyboard methods with a debounce, so they don't fire on every user keystroke.
     * {@link https://github.com/stimulus-use/stimulus-use/blob/master/docs/use-debounce.md}
     */
  static get debounces() {
    return [
      'toggleMenuBasedOnInputValue',
      'updateLiveRegionText',
      {
        name: 'positionListTarget',
        wait: 50
      }
    ]
  }

  /**
     * Set the minimum trigger value length, for use when the trigger is an input element.
     */
  connect() {
    useDispatch(this)
    useDebounce(this, { wait: 200 })
    useWindowResize(this)
    this.triggerMinimumValueLength = 1
    this.initialListLength = this.focusableTargets.length
  }

  /**
     * Change the live region text, so screen readers can announce that change.
     * Re-assign the number of options, so we can use it for comparison again later.
     * @param {count} number
     */
  updateLiveRegionText(count) {
    const announcement = I18n.t('organism.menu.results', { count })
    $(this.liveRegionTarget).text(announcement)
    this.initialListLength = this.focusableTargets.length
  }

  /**
     * Respond to the "results-loaded" event, from the Collection Select component
     * @see {CollectionSelect.renderResults}
     */
  handleMenuItemsLoaded() {
    if (this.hasFocusableTarget) {
      $(this.focusableTargets).each(function(index, element) {
        $(element).attr('tabindex', '0')
      })
      if (this.initialListLength !== this.focusableTargets.length) {
        this.updateLiveRegionText(this.focusableTargets.length)
      }
    } else {
      this.updateLiveRegionText(0)
    }

    this.positionListTarget()
  }

  /**
     * Positions list target according to viewport size.
     */
  positionListTarget() {
    if (this.menuIsOpen()) {
      const threshold = 686 // As specified in AC-521
      let topAmount = ''

      if ($(window).outerHeight() < threshold) {
        // offset includes the label height so that the label is not clipped.
        const offset = ($(this.listTarget).outerHeight() + $(this.labelContentTarget).outerHeight()) * -1
        topAmount = `${offset}px`
      }

      $(this.listTarget).css('top', topAmount)
    }
  }

  /**
     * Open sesame!
     */
  openMenu() {
    $(this.listTarget).removeAttr('hidden')
    $(this.triggerTarget).attr('aria-expanded', 'true')
  }

  /**
     * Close...sesame.
     */
  closeMenu() {
    if (this.hasListTarget) {
      $(this.listTarget).attr('hidden', '')
    }

    if (this.hasTriggerTarget) {
      $(this.triggerTarget).attr('aria-expanded', 'false')
    }

    if (this.hasFocusableTarget) {
      $(this.focusableTargets).each(function(index, element) {
        $(element).attr('tabindex', '-1')
      })
    }
  }

  /**
     * Determine if the menu is already open.
     * @return {boolean}
     */
  menuIsOpen() {
    return !this.listTarget.hasAttribute('hidden')
  }

  /**
     * When the trigger is an input element (which accepts a value),
     * only show the menu when the value meets the minimum character requirement.
     */
  toggleMenuBasedOnInputValue() {
    if (this.triggerTarget.value.length >= this.triggerMinimumValueLength) {
      this.openMenu()
    } else {
      this.closeMenu()
    }
  }

  /**
     * Open or close the menu as needed, based on the trigger element type.
     * Most likely, the trigger will be an input or a button.
     */
  toggleMenu() {
    if (this.triggerTarget.tagName === 'INPUT') {
      this.toggleMenuBasedOnInputValue()
    } else if (this.menuIsOpen()) {
      this.closeMenu()
    } else {
      this.openMenu()
    }
  }

  /**
     * Close the menu when someone clicks on a part of the document
     * that isn't the menu trigger, list, or list item.
     * @param {MouseEvent} event
     */
  handleDocumentClick(event) {
    const thisElementParts = (this.hasTriggerTarget && event.target === this.triggerTarget) || (this.hasListTarget && this.listTarget.contains(event.target))
    if (!thisElementParts) {
      this.closeMenu()
    }
  }

  /**
     * When the user selects an option, close the menu.
     * @param {(MouseEvent|KeyboardEvent)} event
     */
  selectItem(event) {
    // TODO: Add this back in once we have code that updates this on the JS & server-rendered sides.
    // $(event.target).attr("aria-selected", "true");
    // $(event.target).attr("aria-checked", "true");
    this.dispatch('itemSelected')
    this.closeMenu()
  }

  /**
     * Determine what to do when the user presses a keyboard key on the trigger
     * @param {KeyboardEvent} event
     */
  handleTriggerKeydown(event) {
    // Grab event.key for IE11
    const key = event.code || event.key

    if (key === keys.DOWN && this.menuIsOpen()) {
      $(this.focusableTargets).first().focus()
    } else if (key === keys.TAB && this.menuIsOpen()) {
      this.closeMenu()
    } else if (key === keys.ENTER) {
      event.preventDefault()
    } else {
      this.toggleMenuBasedOnInputValue()
    }
  }

  /**
     * Determine what to do when the user presses a keyboard key on an item
     * @param {KeyboardEvent} event
     * {@link https://adamsilver.io/blog/building-an-accessible-autocomplete-control/#menu-interaction-via-keyboard}
     */
  handleItemKeydown(event) {
    // Grab event.key for IE11
    const key = event.code || event.key

    if (key === keys.DOWN && $(event.target).not($(this.focusableTargets).last())) {
      $(event.target).next().focus()
    } else if (key === keys.UP && $(event.target).is($(this.focusableTargets).first())) {
      $(this.triggerTarget).focus()
    } else if (key === keys.UP) {
      $(event.target).prev().focus()
    } else if (key === keys.ENTER || key === keys.SPACE) {
      event.preventDefault()
      this.selectItem(event)
    } else if (key === keys.TAB) {
      this.closeMenu()
    } else if (key === keys.ESCAPE) {
      this.closeMenu()
      $(this.triggerTarget).focus()
    }
  }

  goToPage(event) {
    window.self.location.href = event.target.dataset.url
  }

  windowResize({ width, height, event }) {
    this.positionListTarget()
  }
}
